import { Fragment, useMemo } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, m } from "framer-motion"
import flatMap from "lodash-es/flatMap"
import { ErrorBoundary } from "react-error-boundary"
import { type SetURLSearchParams, useSearchParams } from "react-router-dom"

import {
    Divider,
    EDividerColor,
    ETypographyColor,
    ETypographySize,
    ErrorHint,
    HelpHint,
    Loadable,
    Typography
} from "@/3514/components"
import { FeedbackModal } from "@/3514/modals"
import { type ICoachingSessionModel, ICoachingSessionParticipantModel } from "@/3514/types"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { useCoachGroupSessionSummaryConfig as useQuery } from "./coach.group.api.queries.hooks"
import AssociatedSession from "./coach.group.component.associated-session"
import Heading from "./coach.group.component.heading"
import IndividualsEntity from "./coach.group.entity.individuals"
import SummaryEntity from "./coach.group.entity.summary"
import styles from "./coach.group.styles"
import {
    type TCoachGroupSessionNavigationConfig as INavigationConfig,
    type TCoachGroupSessionUiModel
} from "./coach.group.ui.types"

const { summaryTab: classes } = styles

type _TProps = { session: ICoachingSessionModel | undefined; navigationConfig: INavigationConfig[] }
const CoachGroupSummaryTab: FC<_TProps> = ({ session: initialSession, navigationConfig }: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const sessionIdParam: number = Number(searchParams.get("sessionId"))

    const { data: sessionData, isError: isSessionDataError }: ReturnType<typeof useQuery> = useQuery()

    const combinedSessionData: TCoachGroupSessionUiModel = useMemo<TCoachGroupSessionUiModel>(
        (): TCoachGroupSessionUiModel => ({ ...sessionData, ...initialSession, navigationConfig }),
        [sessionData, initialSession, navigationConfig]
    )

    const individualsIds: number[] = useMemo<number[]>(
        (): number[] =>
            flatMap<ICoachingSessionParticipantModel, number>(
                combinedSessionData.participants,
                (p: ICoachingSessionParticipantModel): number => p.id
            ),
        [combinedSessionData.participants]
    )

    return (
        <div
            className={classes.container({
                isEmptyData: !sessionIdParam,
                isError: isSessionDataError
            })}
        >
            <Heading navConfig={combinedSessionData.navigationConfig} />

            <AnimatePresence>
                {!sessionIdParam ? (
                    <Typography
                        className={classes.emptyHint()}
                        text={t("coachSide.groupPage.summaryTab.emptyParam")}
                        color={ETypographyColor.Dark}
                        size={ETypographySize.Small}
                    />
                ) : (
                    <ErrorBoundary fallback={<ErrorHint isVisible containerClassName="mb-auto mt-0" />}>
                        <m.div
                            key={`gcs-c-${sessionIdParam}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className={classes.rowWrapper()}
                        >
                            <div
                                className={classes.row({
                                    isSingleCol:
                                        !combinedSessionData.isSummaryAvailable ||
                                        !combinedSessionData.areModulesEnabled,
                                    withExtraBottomMargin:
                                        !combinedSessionData.areModulesEnabled ||
                                        !combinedSessionData.withSummaryUnavailableWarning
                                })}
                            >
                                <ErrorBoundary fallback={<ErrorHint isVisible />}>
                                    <Loadable
                                        componentProps={{
                                            isAvailable: combinedSessionData.isSummaryAvailable,
                                            withWarningOnAbsence: combinedSessionData.withSummaryUnavailableWarning
                                        }}
                                        component={SummaryEntity}
                                        loadingFallback={<Fragment>.</Fragment>}
                                        isReady={combinedSessionData.areModulesEnabled}
                                    />
                                </ErrorBoundary>

                                <AssociatedSession session={combinedSessionData} />
                            </div>

                            <ErrorBoundary fallback={<ErrorHint isVisible />}>
                                <Loadable
                                    componentProps={{
                                        isSummaryAvailable: combinedSessionData.isSummaryAvailable,
                                        participantIds: individualsIds
                                    }}
                                    component={IndividualsEntity}
                                    loadingFallback={<Fragment />}
                                    isReady={Boolean(combinedSessionData.areModulesEnabled)}
                                />
                            </ErrorBoundary>

                            {combinedSessionData.areModulesEnabled && (
                                <Divider variant={EDividerColor.LightBlue} margins={{ bottom: "50px", top: "0px" }} />
                            )}
                        </m.div>
                    </ErrorBoundary>
                )}
            </AnimatePresence>

            <HelpHint />

            <FeedbackModal />
        </div>
    )
}

CoachGroupSummaryTab.displayName = "CoachGroupSummaryTab"

export default CoachGroupSummaryTab
